import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import authService from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';

const labelClass = 'text-sm font-medium text-[#1F2937]';
const inputFieldClass =
  'block w-full rounded-md border border-[#D1D5DB] bg-white px-3 py-2.5 text-[#1F2937] placeholder-[#9CA3AF] shadow-none focus:border-[#1a7a33] focus:outline-none focus:ring-1 focus:ring-[#1a7a33] sm:text-sm';

export default function Login() {
  const navigate = useNavigate();
  const { login: setAuthFromLogin } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const result = await authService.login(email, password);
      if (result?.success && result?.data?.user) {
        setAuthFromLogin(result.data);
        toast.success(result.message ?? 'Login successful');
        navigate('/dashboard', { replace: true });
      } else {
        toast.error(result?.message ?? 'Login failed');
      }
    } catch (err) {
      const data = err?.response?.data;
      const msg =
        (typeof data?.message === 'string' && data.message) ||
        data?.error ||
        err?.message ||
        'Something went wrong';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-[400px] px-2 py-3 sm:py-4">
      <div className="rounded-[10px] border border-theme-border-secondary bg-white px-5 py-6 shadow-sm sm:px-6 sm:py-7">
        <div className="mb-6 flex justify-center">
          <img
            src="/svg/altCaseNavLogo.svg"
            alt="altcase"
            className="h-[22px] w-auto sm:h-[26px]"
          />
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className={`block ${labelClass}`}>
              <span className="text-[#EF4444]" aria-hidden>
                *
              </span>{' '}
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`mt-1.5 ${inputFieldClass}`}
              placeholder="Enter email"
            />
          </div>

          <div>
            <label htmlFor="password" className={`block ${labelClass}`}>
              <span className="text-[#EF4444]" aria-hidden>
                *
              </span>{' '}
              Password
            </label>
            <div className="relative mt-1.5">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`${inputFieldClass} pr-10`}
                placeholder="Enter password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded p-1 text-theme-text-secondary hover:text-[#374151] focus:outline-none focus:ring-2 focus:ring-[#1a7a33]/30 focus:ring-offset-1"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <FiEye className="h-5 w-5" /> : <FiEyeOff className="h-5 w-5" />}
              </button>
            </div>
            <Link
              to="/forgot-password"
              className="mt-3 inline-block text-sm font-medium text-[#1F2937] hover:text-theme-text-primary"
            >
              Forgot password?
            </Link>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-lg bg-[#1a7a33] px-4 py-3 text-sm font-bold text-white shadow-[0_4px_0_0_#136629] transition-[transform,box-shadow] hover:bg-[#178a2e] active:translate-y-0.5 active:shadow-[0_2px_0_0_#136629] disabled:pointer-events-none disabled:opacity-60"
          >
            {isSubmitting ? 'Submitting…' : 'Submit'}
          </button>
        </form>
      </div>
    </div>
  );
}
